import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { DndContext, rectIntersection, useSensors } from '@dnd-kit/core';
import dayjs from 'dayjs';

import { ContainerLoading, ErrorResponse, PageNotFound } from '~components';
import { faQuestionCircle } from '~components/icon';

import type { ApiError } from '~libs/axiosWrapper';
import { getErrorMessage } from '~libs/axiosWrapper';
import { dateInputFieldFormat } from '~libs/dayjs';
import { Appointment, deriveTitleFactory } from '~weekly-planner/lib/common';

import ContainerHeader from '~components/ContainerHeader';
import ContainerPanel from '~components/ContainerPanel';
import WorkerCell from '~weekly-planner/components/main/WorkerCell';
import { View as WeeklyView } from '~weekly-planner/components/main/weekly';

import { setTitle } from '~main/actions/login';
import { toggleQuickMenu } from '~main/reducers/app';
import { patchParams, resetWeeklyPlannerState } from '~weekly-planner/reducers/weeklyPlanner';

import { useGetRoreSessionQuery } from '~rore/actions/sessions';
import { SessionStatus, TERMINAL_STATUSES } from '~rore/types';
import { SPOOFED_WORKERS } from '~rore/lib/spoofData';

import useSSE from '~hooks/useSSE';

import RoREBanner from './RoREBanner';
import RoRESideBar from './RoRESideBar';

const SESSION_NOT_FOUND = {
  header: 'Session not found',
  descriptor: 'The session you are looking for does not exist or may have expired.',
  icon: faQuestionCircle,
};

const RoreSessionViewer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const sensors = useSensors(); // no sensors = drag disabled
  const [isTerminal, setIsTerminal] = useState(false);

  const { data: session, isLoading, isError, error, refetch } = useGetRoreSessionQuery(id ?? skipToken, {
    refetchOnMountOrArgChange: true,
  });

  // SSE: listen for solver completion events and refetch session data
  const onSSEEvent = useCallback(
    (event: { type: string; ref: string; ts: string }) => {
      if (event.ref === id && (event.type === 'rore.session.completed' || event.type === 'rore.session.progress')) {
        refetch();
      }
    },
    [id, refetch],
  );

  useSSE({
    channels: id ? [`session:${id}`] : [],
    onEvent: onSSEEvent,
    enabled: !!id && !isTerminal,
  });

  useEffect(() => {
    if (session?.status && TERMINAL_STATUSES.includes(session.status)) {
      setIsTerminal(true);
    }
  }, [session?.status]);

  useEffect(() => {
    dispatch(toggleQuickMenu(true));
    dispatch(
      patchParams({
        date: dayjs().format(dateInputFieldFormat),
        viewType: 'Users',
        rangeType: 'Weekly',
        limit: 30,
      }),
    );
    setTitle(`RoR.E Session — ${id ?? ''}`);

    return () => {
      dispatch(resetWeeklyPlannerState({}));
    };
  }, []);

  const getAppointmentTitle = deriveTitleFactory<Appointment, 'client'>('client');

  if (isLoading) {
    return (
      <div className="p-2 weekly-planner rore-session-viewer" data-cy="rore-session-viewer-container">
        <ContainerHeader>RoR.E Session Viewer</ContainerHeader>
        <ContainerLoading loading />
      </div>
    );
  }

  if (isError) {
    const apiError = error as ApiError;
    if (apiError?.status === 404) {
      return <PageNotFound content={SESSION_NOT_FOUND} />;
    }
    return (
      <div className="p-2 weekly-planner rore-session-viewer" data-cy="rore-session-viewer-container">
        <ContainerHeader>RoR.E Session Viewer</ContainerHeader>
        <ErrorResponse message={getErrorMessage(error)} dataCy="rore-session-viewer-error" />
      </div>
    );
  }

  const status: SessionStatus = session?.status ?? SessionStatus.Processing;
  const createdAt = session?.created_at
    ? dayjs(session.created_at).format('YYYY-MM-DD HH:mm')
    : dayjs().format('YYYY-MM-DD HH:mm');

  return (
    <div className="p-2 weekly-planner rore-session-viewer" data-cy="rore-session-viewer-container">
      <ContainerHeader>RoR.E Session Viewer</ContainerHeader>

      <RoREBanner sessionId={id} createdAt={createdAt} status={status} />

      <DndContext sensors={sensors} collisionDetection={rectIntersection}>
        <ContainerPanel className="main-view" sidePanel={<RoRESideBar />}>
          <WeeklyView
            headerCell={WorkerCell}
            headerTitle="Care Worker"
            rows={SPOOFED_WORKERS}
            loadingState="fulfilled"
            getAppointmentTitle={getAppointmentTitle}
          />
        </ContainerPanel>
      </DndContext>
    </div>
  );
};

export default RoreSessionViewer;
